import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2, MailCheck } from 'lucide-react'
import { useAuth } from '@/providers/auth-provider'
import { resetRequestSchema, type ResetRequestInput } from '@/lib/validations'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Field } from '@/components/ui/field'
import { AuthShell } from './auth-shell'

export function ResetRequestPage() {
  const { requestPasswordReset } = useAuth()
  const [sent, setSent] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetRequestInput>({ resolver: zodResolver(resetRequestSchema) })

  async function onSubmit(values: ResetRequestInput) {
    setSubmitting(true)
    try {
      await requestPasswordReset(values.email)
      setSent(true)
    } catch {
      // Não revelamos se o e-mail existe (evita enumeração de contas).
      setSent(true)
    } finally {
      setSubmitting(false)
    }
  }

  if (sent) {
    return (
      <AuthShell title="Verifique seu e-mail">
        <div className="flex flex-col items-center gap-3 text-center">
          <MailCheck className="h-8 w-8 text-success" />
          <p className="text-sm text-muted-foreground">
            Se houver uma conta com esse e-mail, enviamos um link para redefinir a senha.
          </p>
          <Button asChild variant="outline" className="mt-2">
            <Link to="/entrar">Voltar para o login</Link>
          </Button>
        </div>
      </AuthShell>
    )
  }

  return (
    <AuthShell title="Recuperar senha" subtitle="Enviaremos um link de redefinição.">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <Field label="E-mail" htmlFor="email" error={errors.email?.message}>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            placeholder="voce@empresa.com"
            {...register('email')}
          />
        </Field>
        <Button type="submit" className="w-full" disabled={submitting}>
          {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
          Enviar link
        </Button>
      </form>
      <div className="mt-4 text-center text-sm">
        <Link to="/entrar" className="text-primary hover:underline">
          Voltar para o login
        </Link>
      </div>
    </AuthShell>
  )
}
